import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { FlightCheckAnalysis } from '../../../components/flight-check/FlightCheckAnalysis.js';
import { FlightCheckPage } from '../../../components/flight-check/FlightCheckPage.js';
import {
  CenteredSpinner,
  EmptyState,
  LoadError,
} from '../../../components/flight-check/FlightCheckParts.js';
import { toAnalysisView } from '../../../components/flight-check/flightCheckView.js';
import { useTRPC } from '../../../lib/trpc.js';

const SEARCH_DEBOUNCE_MS = 300;
const BATCH_LIMIT = 100;

function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// Hidden: no nav item links here.
export function FlightCheckAnalysisRoute(): ReactNode {
  const trpc = useTRPC();
  const [search, setSearch] = useState('');
  const query = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);
  const batches = useQuery({
    ...trpc.flightCheck.batches.queryOptions({
      limit: BATCH_LIMIT,
      ...(query === '' ? {} : { search: query }),
    }),
    placeholderData: keepPreviousData,
  });
  const copy = batches.data?.flightCheckCopy;

  let body: ReactNode;
  if (batches.isPending) body = <CenteredSpinner />;
  else if (batches.isError) body = <LoadError onRetry={() => void batches.refetch()} />;
  else if (
    query === '' &&
    search.trim() === '' &&
    batches.data.batches.length === 0 &&
    batches.data.latestBatch === null
  ) {
    body = (
      <EmptyState
        title={batches.data.flightCheckCopy.emptyTitle}
        body={batches.data.flightCheckCopy.emptyBody}
      />
    );
  } else {
    body = (
      <FlightCheckAnalysis
        data={toAnalysisView(batches.data)}
        copy={batches.data.flightCheckCopy}
        search={search}
        onSearchChange={setSearch}
      />
    );
  }

  return (
    <FlightCheckPage title={copy?.title} subtitle={copy?.subtitle}>
      {body}
    </FlightCheckPage>
  );
}

export const Route = createFileRoute('/flight-check/analysis/')({
  component: FlightCheckAnalysisRoute,
});
